import type React from 'react';
import { useState, useCallback } from 'react';
import {
  Button,
  Dialog,
  DialogActions,
  DialogBody,
  DialogContent,
  DialogSurface,
  DialogTitle,
  Dropdown,
  Option,
  Spinner,
  Textarea,
  Text,
  Tooltip,
} from '@fluentui/react-components';
import {
  ChatRegular,
  PersonRegular,
  PersonAddRegular,
  OpenRegular,
  TagRegular,
  WarningRegular,
} from '@fluentui/react-icons';
import { useTranslation } from 'react-i18next';
import type { Incident, IncidentComment, StatusPanelSettings } from 'types';
import { IncidentState } from 'types';
import {
  addIncidentComment,
  extractApiErrorMessage,
  getIncidentComments,
  takeoverIncident,
  updateIncidentStatus,
} from 'services';
import { useAuth, useIncidentSync } from 'hooks';
import { useToast } from 'contexts';
import { useIncidentActionsStyles } from './IncidentTable.styles';
import { IncidentTagsDialog } from './IncidentTagsDialog';

interface IncidentActionsProps {
  incident: Incident;
  settings: StatusPanelSettings;
  onActionComplete?: () => void;
  onAssignClick?: (incident: Incident) => void;
  size?: 'small' | 'medium' | 'large';
}

type CloseClassificationOption = {
  key: string;
  classification: 'Undetermined' | 'TruePositive' | 'BenignPositive' | 'FalsePositive';
  classificationReason?: 'SuspiciousActivity' | 'SuspiciousButExpected' | 'IncorrectAlertLogic' | 'InaccurateData';
  labelKey: string;
};

export const IncidentActions: React.FC<IncidentActionsProps> = ({
  incident,
  settings,
  onActionComplete,
  onAssignClick,
  size = 'small',
}) => {
  const styles = useIncidentActionsStyles();
  const { t } = useTranslation();
  const { canTakeOwnership, canAssignOwnership } = useAuth();
  const waitForIncidentSync = useIncidentSync();
  const { showToast } = useToast();
  const [loading, setLoading] = useState(false);
  const [closeDialogOpen, setCloseDialogOpen] = useState(false);
  const [commentsDialogOpen, setCommentsDialogOpen] = useState(false);
  const [selectedCloseClassification, setSelectedCloseClassification] = useState('truePositiveSuspiciousActivity');
  const [closeComment, setCloseComment] = useState('');
  const [comments, setComments] = useState<IncidentComment[]>([]);
  const [commentsLoading, setCommentsLoading] = useState(false);
  const [commentDraft, setCommentDraft] = useState('');
  const [commentSubmitting, setCommentSubmitting] = useState(false);
  const [tagsDialogOpen, setTagsDialogOpen] = useState(false);

  const closeClassificationOptions: CloseClassificationOption[] = [
    {
      key: 'truePositiveSuspiciousActivity',
      classification: 'TruePositive',
      classificationReason: 'SuspiciousActivity',
      labelKey: 'statusPanel.classification.options.truePositiveSuspiciousActivity',
    },
    {
      key: 'benignPositiveSuspiciousButExpected',
      classification: 'BenignPositive',
      classificationReason: 'SuspiciousButExpected',
      labelKey: 'statusPanel.classification.options.benignPositiveSuspiciousButExpected',
    },
    {
      key: 'falsePositiveIncorrectAlertLogic',
      classification: 'FalsePositive',
      classificationReason: 'IncorrectAlertLogic',
      labelKey: 'statusPanel.classification.options.falsePositiveIncorrectAlertLogic',
    },
    {
      key: 'falsePositiveInaccurateData',
      classification: 'FalsePositive',
      classificationReason: 'InaccurateData',
      labelKey: 'statusPanel.classification.options.falsePositiveInaccurateData',
    },
    {
      key: 'undetermined',
      classification: 'Undetermined',
      labelKey: 'statusPanel.classification.options.undetermined',
    },
  ];

  const extractErrorMessage = useCallback(
    (error: unknown): string =>
      extractApiErrorMessage(error, t('statusPanel.errors.unknownError', 'An unknown error occurred')),
    [t],
  );

  const handleAction = async (
    action: () => Promise<unknown>,
    actionName: string,
    predicate?: (state: IncidentState) => boolean,
  ): Promise<void> => {
    try {
      setLoading(true);
      await action();
      await waitForIncidentSync({
        incidentId: incident.incidentId,
        predicate: predicate
          ? (message) => {
              const nextState = message.incident?.state ?? message.state;
              return predicate(nextState);
            }
          : undefined,
      });

      showToast({
        title: t('statusPanel.sync.updateConfirmedTitle'),
        body: t('statusPanel.sync.updateConfirmedBody'),
        intent: 'success',
        timeout: 3000,
      });

      onActionComplete?.();
    } catch (err) {
      console.error('Action failed:', err);
      const errorMessage = extractErrorMessage(err);
      showToast({
        title: t('statusPanel.errors.actionFailed', { action: actionName }),
        body: errorMessage,
        intent: 'error',
        timeout: 10000,
      });
    } finally {
      setLoading(false);
    }
  };

  // Check if incident state allows takeover
  const incidentAllowsTakeover =
    incident.state === IncidentState.New ||
    incident.state === IncidentState.Active ||
    !incident.assignedOperatorId;

  // Check if incident state allows assignment
  const incidentAllowsAssign = incident.state !== IncidentState.Closed;

  // Combine incident state checks with user permissions
  const showTakeoverButton = incidentAllowsTakeover && canTakeOwnership;
  const showAssignButton = incidentAllowsAssign && canAssignOwnership;

  const isNewStatus = incident.state === IncidentState.New;
  const isClosedStatus = incident.state === IncidentState.Closed;
  const showCloseButton = !isNewStatus && !isClosedStatus;
  const showReopenButton = isClosedStatus;

  const handleCloseWithClassification = async (): Promise<void> => {
    const selection = closeClassificationOptions.find((option) => option.key === selectedCloseClassification);
    if (!selection) {
      return;
    }

    const trimmedComment = closeComment.trim();
    setCloseDialogOpen(false);

    await handleAction(
      async () => {
        await updateIncidentStatus(
          incident.incidentId,
          'Closed',
          selection.classification,
          selection.classificationReason,
          trimmedComment || t(selection.labelKey),
        );
        // Persist the operator's note as a regular incident comment so it's
        // visible in the comments thread, not only in Sentinel's close metadata.
        if (trimmedComment) {
          try {
            await addIncidentComment(incident.incidentId, trimmedComment);
          } catch (commentErr) {
            // Closing already succeeded; failing the comment shouldn't roll back.
            console.error('Failed to attach close comment:', commentErr);
          }
        }
      },
      t('statusPanel.actions.close'),
      (nextState) => nextState === IncidentState.Closed,
    );

    setCloseComment('');
  };

  const loadComments = useCallback(async () => {
    setCommentsLoading(true);
    try {
      const response = await getIncidentComments(incident.incidentId);
      setComments(response.data ?? []);
    } catch (err) {
      const errorMessage = extractErrorMessage(err);
      showToast({
        title: t('statusPanel.comments.loadError'),
        body: errorMessage,
        intent: 'error',
        timeout: 8000,
      });
    } finally {
      setCommentsLoading(false);
    }
  }, [extractErrorMessage, incident.incidentId, showToast, t]);

  const openCommentsDialog = useCallback(async () => {
    setCommentsDialogOpen(true);
    await loadComments();
  }, [loadComments]);

  const handleAddComment = async (): Promise<void> => {
    const message = commentDraft.trim();
    if (!message) {
      return;
    }

    setCommentSubmitting(true);
    try {
      await addIncidentComment(incident.incidentId, message);
      await waitForIncidentSync({ incidentId: incident.incidentId });
      setCommentDraft('');
      await loadComments();

      showToast({
        title: t('statusPanel.comments.addSuccessTitle'),
        body: t('statusPanel.sync.updateConfirmedBody'),
        intent: 'success',
        timeout: 3000,
      });

      onActionComplete?.();
    } catch (err) {
      const errorMessage = extractErrorMessage(err);
      showToast({
        title: t('statusPanel.comments.addError'),
        body: errorMessage,
        intent: 'error',
        timeout: 8000,
      });
    } finally {
      setCommentSubmitting(false);
    }
  };

  if (loading) {
    return <Spinner size={size === 'small' ? 'tiny' : 'small'} />;
  }

  return (
    <div className={styles.root}>
      {/* Open in Sentinel */}
      {incident.sentinelUrl && (
        <Tooltip content={t('statusPanel.actions.viewInSentinel')} relationship="label">
          <Button
            as="a"
            href={incident.sentinelUrl}
            target="_blank"
            rel="noopener noreferrer"
            icon={<OpenRegular />}
            size={size}
            appearance="subtle"
          />
        </Tooltip>
      )}
      {!incident.sentinelUrl && (
        <Tooltip content={t('statusPanel.actions.sentinelUrlMissing')} relationship="label">
          <Button
            icon={<WarningRegular />}
            size={size}
            appearance="subtle"
            disabled
          />
        </Tooltip>
      )}

      {/* Take Ownership - available to operators and admins */}
      {showTakeoverButton && (
        <Tooltip content={t('statusPanel.actions.takeOwnership')} relationship="label">
          <Button
            icon={<PersonRegular />}
            size={size}
            appearance="subtle"
            onClick={() =>
              handleAction(
                () => takeoverIncident(incident.incidentId, 'current-user'),
                t('statusPanel.actions.takeOwnership'),
              )
            }
          />
        </Tooltip>
      )}

      {/* Assign to someone else - only available to admins */}
      {showAssignButton && (
        <Tooltip content={t('statusPanel.actions.assignOwnership')} relationship="label">
          <Button
            icon={<PersonAddRegular />}
            size={size}
            appearance="subtle"
            onClick={() => onAssignClick?.(incident)}
          />
        </Tooltip>
      )}

      <Tooltip content={t('statusPanel.actions.comments')} relationship="label">
        <Button
          icon={<ChatRegular />}
          size={size}
          appearance="subtle"
          onClick={() => void openCommentsDialog()}
        />
      </Tooltip>

      <Tooltip content={t('statusPanel.tags.editTitle')} relationship="label">
        <Button
          icon={<TagRegular />}
          size={size}
          appearance="subtle"
          onClick={() => setTagsDialogOpen(true)}
        />
      </Tooltip>

      {showCloseButton && (
        <Tooltip content={t('statusPanel.actions.close')} relationship="label">
          <Button
            size={size}
            appearance="subtle"
            onClick={() => setCloseDialogOpen(true)}
          >
            {t('statusPanel.actions.close')}
          </Button>
        </Tooltip>
      )}

      {showReopenButton && (
        <Tooltip content={t('statusPanel.actions.reopen')} relationship="label">
          <Button
            size={size}
            appearance="subtle"
            onClick={() =>
              handleAction(
                () => updateIncidentStatus(incident.incidentId, 'Active'),
                t('statusPanel.actions.reopen'),
                (nextState) => nextState !== IncidentState.Closed,
              )
            }
          >
            {t('statusPanel.actions.reopen')}
          </Button>
        </Tooltip>
      )}

      <Dialog open={closeDialogOpen} onOpenChange={(_e, data) => setCloseDialogOpen(data.open)}>
        <DialogSurface>
          <DialogBody>
            <DialogTitle>{t('statusPanel.classification.dialogTitle')}</DialogTitle>
            <DialogContent>
              <Text>{t('statusPanel.classification.dialogDescription')}</Text>
              <Dropdown
                value={t(
                  closeClassificationOptions.find((option) => option.key === selectedCloseClassification)?.labelKey
                  ?? 'statusPanel.classification.options.truePositiveSuspiciousActivity',
                )}
                onOptionSelect={(_e, data) => setSelectedCloseClassification(data.optionValue ?? selectedCloseClassification)}
              >
                {closeClassificationOptions.map((option) => (
                  <Option key={option.key} value={option.key}>
                    {t(option.labelKey)}
                  </Option>
                ))}
              </Dropdown>
              <Text size={300} weight="semibold" style={{ marginTop: 12 }}>
                {t('statusPanel.classification.commentLabel')}
              </Text>
              <Textarea
                value={closeComment}
                placeholder={t('statusPanel.classification.commentPlaceholder')}
                onChange={(_e, data) => setCloseComment(data.value)}
                resize="vertical"
                style={{ width: '100%' }}
                textarea={{ style: { width: '100%', minHeight: 80 } }}
              />
            </DialogContent>
            <DialogActions>
              <Button appearance="secondary" onClick={() => { setCloseDialogOpen(false); setCloseComment(''); }}>
                {t('common.cancel')}
              </Button>
              <Button appearance="primary" onClick={() => void handleCloseWithClassification()}>
                {t('statusPanel.actions.close')}
              </Button>
            </DialogActions>
          </DialogBody>
        </DialogSurface>
      </Dialog>

      <Dialog open={commentsDialogOpen} onOpenChange={(_e, data) => setCommentsDialogOpen(data.open)}>
        <DialogSurface>
          <DialogBody>
            <DialogTitle>{t('statusPanel.comments.title')}</DialogTitle>
            <DialogContent className={styles.commentsDialogContent}>
              <div className={styles.commentsList}>
                {commentsLoading ? (
                  <Spinner size="small" />
                ) : comments.length === 0 ? (
                  <Text>{t('statusPanel.comments.none')}</Text>
                ) : (
                  comments.map((comment) => (
                    <div key={comment.id} className={styles.commentItem}>
                      <Text size={300} weight="semibold">
                        {comment.authorName || t('statusPanel.comments.unknownAuthor')}
                      </Text>
                      <Text size={400} className={styles.commentMessage}>{comment.message}</Text>
                      {comment.createdTimeUtc && (
                        <Text size={200} className={styles.commentTimestamp}>
                          {new Date(comment.createdTimeUtc).toLocaleString()}
                        </Text>
                      )}
                    </div>
                  ))
                )}
              </div>

              <Textarea
                className={styles.commentInput}
                value={commentDraft}
                placeholder={t('statusPanel.comments.placeholder')}
                onChange={(_e, data) => setCommentDraft(data.value)}
                resize="vertical"
              />
            </DialogContent>
            <DialogActions>
              <Button appearance="secondary" onClick={() => setCommentsDialogOpen(false)}>
                {t('common.cancel')}
              </Button>
              <Button
                appearance="primary"
                onClick={() => void handleAddComment()}
                disabled={commentSubmitting || !commentDraft.trim()}
              >
                {commentSubmitting ? t('common.loading') : t('statusPanel.comments.add')}
              </Button>
            </DialogActions>
          </DialogBody>
        </DialogSurface>
      </Dialog>
      <IncidentTagsDialog
        incident={incident}
        settings={settings}
        open={tagsDialogOpen}
        onOpenChange={setTagsDialogOpen}
        onTagsUpdated={() => onActionComplete?.()}
      />
    </div>
  );
};
